import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ContentPageTheme, Player, ReviewPageConfig, ReviewPageConfigServices } from "@/services/bedrock";
import { publicWebOrigin, useDebounced } from "@/utils";
import { useIframePreview } from "../../components/useIframePreview";
import { buildReviewPagePreviewData } from "./reviewPagePreviewData";
import { getReviewPageSettings, reviewPagePreviewParams } from "./reviewPageSettings";

const PLAYER_ID_PLACEHOLDER = "{{player_id}}";
const TEXT_DEBOUNCE_MS = 400;

interface Args {
  config: ReviewPageConfig;
  themes: ContentPageTheme[];
  previewPlayer: Player | null;
}

const useReviewPagePreview = ({ config, themes, previewPlayer }: Args) => {
  const origin = publicWebOrigin();
  const iframeSrc = `${origin}/r/preview`;
  const playerGuid = previewPlayer?.guid;
  const settings = useMemo(() => getReviewPageSettings(config), [config]);
  const headline = useDebounced(settings.headline, TEXT_DEBOUNCE_MS);
  const subtitle = useDebounced(settings.subtitle, TEXT_DEBOUNCE_MS);
  const previewParams = useMemo(
    () => reviewPagePreviewParams({ ...settings, headline, subtitle }).toString(),
    [settings, headline, subtitle]
  );
  const listParams = useMemo(() => {
    const { headline: _headline, subtitle: _subtitle, callToAction: _callToAction, ...listSettings } = settings;
    return reviewPagePreviewParams(listSettings).toString();
  }, [settings]);

  const {
    data: playerData,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["reviewPagePublicPreview", playerGuid, previewParams],
    queryFn: () => ReviewPageConfigServices.getPublicPreview(playerGuid!, new URLSearchParams(previewParams)),
    enabled: !!playerGuid,
    placeholderData: previousData => previousData,
  });

  useEffect(() => {
    if (!isError) return;
    console.error(error);
    toast.error("Error loading player preview");
  }, [isError, error]);

  const message = useMemo(() => {
    if (!playerGuid) {
      return { type: "REVIEW_PAGE", payload: buildReviewPagePreviewData(settings, themes), previewParams: null };
    }
    if (isError || !playerData) return { type: "REVIEW_PAGE", payload: null };

    const selectedTheme = settings.contentPageThemeId
      ? themes.find(theme => theme.id === settings.contentPageThemeId)
      : null;
    return {
      type: "REVIEW_PAGE",
      payload: { ...playerData, ...(selectedTheme ? { theme: selectedTheme } : {}) },
      previewParams: listParams,
    };
  }, [playerGuid, settings, themes, playerData, isError, listParams]);

  const { iframeRef } = useIframePreview({ src: iframeSrc, message });

  const [loadedForGuid, setLoadedForGuid] = useState<string | null>(null);
  useEffect(() => {
    if (!playerGuid) setLoadedForGuid(null);
    else if (!isFetching) setLoadedForGuid(playerGuid);
  }, [playerGuid, isFetching]);

  const playerSegment = playerGuid ? encodeURIComponent(playerGuid) : PLAYER_ID_PLACEHOLDER;
  const pageUrl = `${origin}/r/${playerSegment}`;

  return {
    iframeRef,
    iframeSrc,
    pageUrl,
    isSamplePreview: !playerGuid,
    isLoading: !!playerGuid && playerGuid !== loadedForGuid,
    previewPlayerName: previewPlayer?.fullName || undefined,
  };
};

export { useReviewPagePreview };
